<template>
  <!--
    Export period picker, the layout of the availability export: shortcuts on the
    left, a picker on the right, the chosen range summed up underneath.
    unit="month": month grid, range as ["YYYY-MM", "YYYY-MM"].
    unit="day":   calendar, range as [Date, Date] (whole days).
  -->
  <v-dialog :model-value="modelValue" max-width="42em" @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-toolbar flat color="white" density="compact" class="px-2">
        <v-icon color="primary" class="mr-2">{{ icon }}</v-icon>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold">
          {{ title || $filters.capitalize($t("export")) }}
          <span v-if="subtitle" class="text-body-2 text-medium-emphasis font-weight-regular ml-1">&ndash; {{ subtitle }}</span>
        </v-toolbar-title>
        <v-btn icon="mdi-close" size="small" variant="text" @click="close"></v-btn>
      </v-toolbar>
      <v-divider></v-divider>

      <v-row no-gutters>
        <!-- Shortcuts -->
        <v-col cols="12" sm="5" class="export-shortcuts">
          <v-list density="compact" nav class="py-2">
            <v-list-item
              v-for="preset in allPresets"
              :key="preset.key"
              :active="isPreset(preset)"
              color="primary"
              rounded="lg"
              @click="range = [preset.start, preset.end]"
            >
              <template v-slot:prepend>
                <v-icon size="small">{{ preset.icon }}</v-icon>
              </template>
              <v-list-item-title>{{ preset.label }}</v-list-item-title>
              <v-list-item-subtitle>{{ rangeLabel(preset.start, preset.end) }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-col>

        <!-- Picker -->
        <v-col cols="12" sm="7" class="pa-4">
          <template v-if="unit === 'month'">
            <div class="d-flex align-center justify-space-between mb-2">
              <v-btn icon="mdi-chevron-left" size="small" variant="text" @click="gridYear -= 1"></v-btn>
              <span class="text-subtitle-1 font-weight-bold">{{ gridYear }}</span>
              <v-btn
                icon="mdi-chevron-right"
                size="small"
                variant="text"
                :disabled="gridYear >= currentYear"
                @click="gridYear += 1"
              ></v-btn>
            </div>
            <v-row dense>
              <v-col v-for="month in gridMonths" :key="month.value" cols="3">
                <v-btn
                  block
                  size="small"
                  class="month-btn"
                  :variant="monthState(month.value) === 'edge' ? 'flat' : monthState(month.value) === 'inside' ? 'tonal' : 'text'"
                  :color="monthState(month.value) ? 'primary' : undefined"
                  :disabled="month.value > currentMonth"
                  @click="pickMonth(month.value)"
                >{{ month.label }}</v-btn>
              </v-col>
            </v-row>
          </template>

          <VueDatePicker
            v-else
            v-model="dayRange"
            :locale="$i18n.locale"
            :first-day-of-week="1"
            inline
            auto-apply
            range
            show-week
            :max-date="new Date()"
            :enable-time-picker="false"
          ></VueDatePicker>

          <div class="mt-3 text-body-2">
            <v-icon size="small" class="mr-1">mdi-calendar-range</v-icon>
            <template v-if="valid">
              <span class="font-weight-medium">{{ rangeLabel(sorted[0], sorted[1]) }}</span>
              <span class="text-medium-emphasis ml-2">
                ({{ count }} {{ unit === "month" ? $t("export_range.months", count) : $t("generic.day", count) }})
              </span>
            </template>
            <span v-else class="text-medium-emphasis">{{ $t("snack.no_range") }}</span>
          </div>
        </v-col>
      </v-row>

      <v-divider></v-divider>
      <v-card-actions class="px-4">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="close">{{ $filters.capitalize($t("cancel")) }}</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-download"
          :disabled="!valid"
          :loading="loading"
          @click="confirm"
        >
          {{ $filters.capitalize($t("export")) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import { isNone } from "@/functions.js";

const MONTH = "YYYY-MM";
const DAY = "YYYY-MM-DD";

export default {
  name: "ExportRangeDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    unit: { type: String, default: "day" }, // "day" | "month"
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    icon: { type: String, default: "mdi-download" },
    loading: { type: Boolean, default: false },
    // Extra shortcuts shown first, as {key, label, icon, start, end} in the unit's format
    presets: { type: Array, default: () => [] },
    // Range selected when the dialog opens, in the unit's format; defaults to the first preset
    initialRange: { type: Array, default: null }
  },
  emits: ["update:modelValue", "export"],

  computed: {
    fmt() {
      return this.unit === "month" ? MONTH : DAY;
    },

    currentMonth() {
      return moment().format(MONTH);
    },

    currentYear() {
      return moment().year();
    },

    // Built-in shortcuts for the unit, after the caller's own ones
    allPresets() {
      const now = moment();
      const f = (m) => m.format(this.fmt);
      let builtin;
      if (this.unit === "month") {
        const m0 = now.clone().startOf("month");
        builtin = [
          { key: "last_6", icon: "mdi-history", label: this.$t("export_range.last_6"), start: f(m0.clone().subtract(5, "months")), end: f(m0) },
          { key: "last_12", icon: "mdi-history", label: this.$t("export_range.last_12"), start: f(m0.clone().subtract(11, "months")), end: f(m0) },
          { key: "year", icon: "mdi-calendar-today", label: this.$t("export_range.year"), start: f(m0.clone().startOf("year")), end: f(m0) },
          { key: "last_year", icon: "mdi-calendar-blank", label: this.$t("export_range.last_year"),
            start: f(m0.clone().subtract(1, "year").startOf("year")), end: f(m0.clone().subtract(1, "year").endOf("year")) }
        ];
      } else {
        builtin = [
          { key: "today", icon: "mdi-calendar-today", label: this.$t("export_range.today"), start: f(now), end: f(now) },
          { key: "yesterday", icon: "mdi-calendar-arrow-left", label: this.$t("export_range.yesterday"),
            start: f(now.clone().subtract(1, "day")), end: f(now.clone().subtract(1, "day")) },
          { key: "7d", icon: "mdi-history", label: this.$t("export_range.last_7_days"), start: f(now.clone().subtract(6, "days")), end: f(now) },
          { key: "30d", icon: "mdi-history", label: this.$t("export_range.last_30_days"), start: f(now.clone().subtract(29, "days")), end: f(now) },
          { key: "this_month", icon: "mdi-calendar-month", label: this.$t("export_range.this_month"), start: f(now.clone().startOf("month")), end: f(now) },
          { key: "last_month", icon: "mdi-calendar-blank", label: this.$t("export_range.last_month"),
            start: f(now.clone().subtract(1, "month").startOf("month")), end: f(now.clone().subtract(1, "month").endOf("month")) }
        ];
      }
      return [...this.presets, ...builtin].map((p) => ({ ...p, label: this.$filters.capitalize(p.label) }));
    },

    valid() {
      return Array.isArray(this.range) && this.range.length === 2 && !isNone(this.range[0]) && !isNone(this.range[1]);
    },

    // Range as [start, end] strings, start first
    sorted() {
      if (!this.valid) {
        return [null, null];
      }
      const [a, b] = this.range;
      return a > b ? [b, a] : [a, b];
    },

    count() {
      if (!this.valid) {
        return 0;
      }
      const [start, end] = this.sorted;
      const unit = this.unit === "month" ? "months" : "days";
      return moment(end, this.fmt).diff(moment(start, this.fmt), unit) + 1;
    },

    gridMonths() {
      // The twelve months of the grid year, short names in the app language
      return Array.from({ length: 12 }, (_, i) => {
        const m = moment({ year: this.gridYear, month: i }).locale(this.$i18n.locale);
        return { value: m.format(MONTH), label: m.format("MMM") };
      });
    },

    // The calendar works with Date objects, the range with day strings
    dayRange: {
      get() {
        if (!this.valid) {
          return [];
        }
        return this.sorted.map((d) => moment(d, DAY).toDate());
      },
      set(value) {
        if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
          this.range = [moment(value[0]).format(DAY), moment(value[1]).format(DAY)];
        } else if (Array.isArray(value) && value.length === 1 && value[0]) {
          this.range = [moment(value[0]).format(DAY), moment(value[0]).format(DAY)];
        } else {
          this.range = [null, null];
        }
      }
    }
  },

  methods: {
    isPreset(preset) {
      return this.range[0] === preset.start && this.range[1] === preset.end;
    },

    monthState(month) {
      const [start, end] = this.range;
      if (month === start || month === end) return "edge";
      if (start && end && month > start && month < end) return "inside";
      return "";
    },

    pickMonth(month) {
      // First click: a single month; second click: the other end of the range
      const [start, end] = this.range;
      if (!start || start !== end) {
        this.range = [month, month];
      } else if (month < start) {
        this.range = [month, start];
      } else {
        this.range = [start, month];
      }
    },

    label(value) {
      if (isNone(value)) {
        return "";
      }
      const m = moment(value, this.fmt).locale(this.$i18n.locale);
      const text = this.unit === "month" ? m.format("MMMM YYYY") : m.format("DD/MM/YYYY");
      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    rangeLabel(start, end) {
      if (isNone(start) && isNone(end)) {
        return "";
      }
      return start === end ? this.label(start) : `${this.label(start)} – ${this.label(end)}`;
    },

    close() {
      this.$emit("update:modelValue", false);
    },

    confirm() {
      if (!this.valid) {
        return;
      }
      const [start, end] = this.sorted;
      if (this.unit === "month") {
        this.$emit("export", { start, end });
      } else {
        this.$emit("export", {
          start: moment(start, DAY).startOf("day").toDate(),
          end: moment(end, DAY).endOf("day").toDate()
        });
      }
    }
  },

  watch: {
    modelValue(open) {
      if (!open) {
        return;
      }
      const first = this.allPresets[0];
      this.range = this.initialRange && this.initialRange.length === 2 ? [...this.initialRange] : [first.start, first.end];
      const ref = this.range[1] || this.range[0];
      this.gridYear = ref ? moment(ref, this.fmt).year() : this.currentYear;
    }
  },

  data() {
    return {
      range: [null, null],
      gridYear: moment().year()
    };
  }
};
</script>

<style scoped>
.export-shortcuts {
  background: #fafafa;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
.month-btn {
  text-transform: capitalize;
}
</style>
